import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useToast } from "@/components/ui/use-toast";
import Layout from '@/components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { Plus, Settings } from 'lucide-react';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbSeparator } from "@/components/ui/breadcrumb";

const OrganizationPage = () => {
  const { organizationSlug } = useParams();
  const navigate = useNavigate();
  const { axiosInstance } = useAuth();
  const { toast } = useToast();
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrganization = async () => {
      try {
        const response = await axiosInstance.get(`/organizations/${organizationSlug}`);
        setOrganization(response.data);
      } catch (error) {
        console.error('Error fetching organization:', error);
        toast({
          description: "Failed to fetch organization details",
          variant: "destructive"
        });
      } finally {
        setLoading(false);
      }
    };

    fetchOrganization();
  }, [organizationSlug]);

  if (loading) {
    return <Layout>Loading...</Layout>;
  }

  if (!organization) {
    return <Layout>Organization not found</Layout>;
  }

  const canManageOrg = ['admin', 'owner'].includes(organization.role);

  return (
    <Layout>
      <div className="space-y-6">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/today">Today</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink>{organization.name}</BreadcrumbLink>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">{organization.name}</h1>
            <p className="text-muted-foreground">Organization Overview</p>
          </div>
          {canManageOrg && (
            <Button
              variant="outline"
              onClick={() => navigate(`/${organizationSlug}/settings`)}
            >
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </Button>
          )}
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {/* Projects Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Card className="h-full">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <div className="space-y-1">
                  <CardTitle>Projects</CardTitle>
                  <CardDescription>{organization.projects.length} total projects</CardDescription>
                </div>
                {canManageOrg && (
                  <Button size="sm" onClick={() => navigate('/plan/new')}>
                    <Plus className="w-4 h-4 mr-2" />
                    New
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {organization.projects.slice(0, 5).map((project) => (
                    <div
                      key={project.id}
                      onClick={() => navigate(`/${organizationSlug}/plan/${project.key}/board`)}
                      className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-accent cursor-pointer"
                    >
                      <div>
                        <p className="font-medium">{project.name}</p>
                        <p className="text-sm text-muted-foreground">{project.taskCount} tasks</p>
                      </div>
                      <span className="text-sm text-muted-foreground">{project.key}</span>
                    </div>
                  ))}
                  {organization.projects.length > 5 && (
                    <Button 
                      variant="ghost" 
                      className="w-full"
                      onClick={() => navigate('/plan')}
                    >
                      View all projects
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Monitors Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            <Card className="h-full">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <div className="space-y-1">
                  <CardTitle>Monitors</CardTitle>
                  <CardDescription>{organization.monitors.length} total monitors</CardDescription>
                </div>
                {canManageOrg && (
                  <Button size="sm" onClick={() => navigate('/monitors/new')}>
                    <Plus className="w-4 h-4 mr-2" />
                    New
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {organization.monitors.slice(0, 5).map((monitor) => (
                    <div
                      key={monitor.id}
                      onClick={() => navigate(`/monitors/${monitor.id}`)}
                      className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-accent cursor-pointer"
                    >
                      <div className="space-y-1">
                        <p className="font-medium">{monitor.name}</p>
                        <div className="flex items-center">
                          <div className="relative">
                            <div className={`w-2.5 h-2.5 rounded-full ${
                              monitor.status.toLowerCase() === 'running' ? 'bg-emerald-400' : 'bg-yellow-500'
                            }`} />
                            {monitor.status.toLowerCase() === 'running' && (
                              <div className="absolute inset-0 w-2.5 h-2.5 bg-emerald-400 rounded-full animate-ping" />
                            )}
                          </div>
                          <span className="ml-2 text-sm text-muted-foreground">{monitor.status}</span>
                        </div>
                      </div>
                      <span className="text-sm text-muted-foreground">{monitor.interval}m</span>
                    </div>
                  ))}
                  {organization.monitors.length > 5 && (
                    <Button 
                      variant="ghost" 
                      className="w-full"
                      onClick={() => navigate('/monitors')}
                    >
                      View all monitors
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Members Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
          >
            <Card className="h-full">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <div className="space-y-1">
                  <CardTitle>Team Members</CardTitle>
                  <CardDescription>{organization.members.length} total members</CardDescription>
                </div>
                {canManageOrg && (
                  <Button 
                    size="sm" 
                    onClick={() => navigate(`/${organizationSlug}/settings/members`)}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Invite
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {organization.members.slice(0, 5).map((member) => (
                    <div
                      key={member.id}
                      className="flex items-center justify-between p-3 rounded-lg border bg-card"
                    >
                      <div>
                        <p className="font-medium">{member.name}</p>
                        <p className="text-sm text-muted-foreground">{member.email}</p>
                      </div>
                      <span className="text-sm capitalize bg-primary/10 text-primary px-2 py-1 rounded">
                        {member.role}
                      </span>
                    </div>
                  ))}
                  {organization.members.length > 5 && (
                    <Button 
                      variant="ghost" 
                      className="w-full"
                      onClick={() => navigate(`/${organizationSlug}/settings/members`)}
                    >
                      View all members
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </Layout>
  );
};

export default OrganizationPage; 